import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import Footer from "../../../../common/Footer";
import Header from "../../../../common/Header";
import { useNavigate, useParams } from "react-router-dom";
import { config } from "../../../../config/config";
import { apiList, invokeApi } from "../../../../services/apiServices";
import { useCookies } from "react-cookie";

const ScrollContainer = styled("div")(({ theme }) => ({
  display: "flex",
  overflowX: "auto",
  overflowY: "hidden",
  scrollSnapType: "x mandatory",
  scrollBehavior: "smooth",
  height: "100%",
  width: "100%",
  position: "relative",
  margin: 0,
  padding: 0,
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  [theme.breakpoints.down("md")]: {
    height: "100%",
    width: "100%",
  },
}));

const ScrollItem = styled("div")({
  flex: "0 0 100%",
  scrollSnapAlign: "start",
  height: "100%",
  position: "relative",
  margin: 0,
  padding: 0,
});

const ScrollImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  margin: 0,
  padding: 0,
});

const DotsContainer = styled("div")({
  position: "absolute",
  bottom: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  zIndex: 10,
  pointerEvents: "auto",
});

const Dot = styled("div")(({ active }) => ({
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  cursor: "pointer",
  backgroundColor: active ? "#fff" : "rgba(255,255,255,0.5)",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: "white",
    transform: "scale(1.2)",
  },
}));

const FeatureDetailPage = () => {
  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [featureData, setFeatureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { ogUrl } = useParams();
  const [cookies] = useCookies();

  // Fetch features list on mount, then find matching feature by ogUrl param
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await invokeApi(
          config.getMyCollege + apiList.getFeatures, // note plural getFeatures
          null,
          cookies
        );

        if (response?.status === 200) {
          let features = [];

          if (Array.isArray(response.data)) {
            features = response.data;
          } else if (response.data?.features && Array.isArray(response.data.features)) {
            features = response.data.features;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            features = response.data.data;
          } else if (response.data?.items && Array.isArray(response.data.items)) {
            features = response.data.items;
          }

          if (features.length > 0) {
            // Find feature matching the ogUrl param
            const matchedFeature = features.find((f) => f.ogUrl === ogUrl) || features[0];

            setFeatureData({
              id: matchedFeature.id,
              featureTitle: matchedFeature.featureTitle || "",
              featureContent: matchedFeature.featureContent || "",
              // Use featuredUrl instead of bannerUrl
              bannerUrl: matchedFeature.featuredUrl || "/default-banner.jpg",
              ogUrl: matchedFeature.ogUrl || "",
            });
          } else {
            setError("No features found.");
          }
        } else {
          setError(`Failed to fetch features: ${response?.status}`);
        }
      } catch (error) {
        console.error("API error:", error);
        setError("Failed to load feature data");
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
    window.scroll(0, 0);
  }, [ogUrl, cookies]);

  // Build images array from featuredUrl (stored in bannerUrl here)
  const images = featureData?.bannerUrl ? [featureData.bannerUrl] : [];

  // Auto-scroll interval
  useEffect(() => {
    if (images.length <= 1) return; // Don't auto-scroll if only one image

    const container = scrollRef.current;
    if (!container) return;

    const interval = setInterval(() => {
      const next = (currentIndex + 1) % images.length;
      setCurrentIndex(next);
      container.scrollTo({
        left: container.clientWidth * next,
        behavior: "smooth",
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, images.length]);

  // Sync dot & scroll
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || images.length === 0) return;

    const onScroll = () => {
      const scrollLeft = container.scrollLeft;
      const width = container.offsetWidth;
      const idx = Math.round(scrollLeft / width);
      setCurrentIndex(idx);
    };

    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [images.length]);

  const handleDotClick = (index) => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({
      left: container.clientWidth * index,
      behavior: "smooth",
    });
    setCurrentIndex(index);
  };

  if (loading) {
    return (
      <>
        <Header />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            flexDirection: "column",
            p: 3,
          }}
        >
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1">Please check your connection and try again.</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Grid container>
        <Header />

        {/* Hero / Banner Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            width: "100%",
            height: { xs: "50vh", md: "65vh" },
            position: "relative",
          }}
        >
          {images.length > 0 ? (
            <>
              <ScrollContainer ref={scrollRef}>
                {images.map((img, idx) => (
                  <ScrollItem key={idx}>
                    <ScrollImage
                      src={img}
                      alt={`${featureData?.featureTitle || "Feature"} Banner ${idx + 1}`}
                      onError={(e) => {
                        e.target.src = "/default-banner.jpg";
                      }}
                    />
                  </ScrollItem>
                ))}
              </ScrollContainer>
              {/* Only show dots if multiple images */}
              {images.length > 1 && (
                <DotsContainer>
                  {images.map((_, idx) => (
                    <Dot key={idx} active={currentIndex === idx} onClick={() => handleDotClick(idx)} />
                  ))}
                </DotsContainer>
              )}
            </>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "#f5f5f5",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" color="textSecondary">
                No banner image available
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Content Section */}
        <Grid item xs={12} md={12}>
          <Grid
            sx={{
              color: "#7f6b56",
              fontFamily: "Inter, sans-serif",
              px: { xs: 2, sm: 3 },
              py: { xs: 4, sm: 6 },
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              maxWidth={{ xs: "100%", sm: "md" }}
              width="100%"
              textAlign="center"
              sx={{
                border: "1px solid #7f6b56",
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                boxSizing: "border-box",
              }}
            >
              {/* Feature content */}
              <Typography
                variant="body1"
                gutterBottom
                sx={{
                  mt: 1,
                  lineHeight: 1.6,
                  fontFamily: "Montserrat, sans-serif",
                  textAlign: "left",
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  fontWeight: 500,
                  whiteSpace: "pre-line",
                }}
                dangerouslySetInnerHTML={{
                  __html: featureData?.featureContent || "No content available",
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Footer variant="light" />
    </>
  );
};

export default FeatureDetailPage;
